import { formatDateDdMmYyyy, parseDateIso } from '@/utils/operationReportDisplay';

// Список статусов, доступных в фильтре отчёта по претензиям.
// Отдельный от `PretensionStatus` enum, т.к. enum отражает контракт frontend-домена
// (создание/редактирование/действия), а в отчёте могут фигурировать статусы,
// приходящие только с бэка (например, 'disputed' — backlog для frontend-enum).
// 8 канонических статусов в порядке таблицы. Это видимый бизнес-набор; фоновые
// (created/draft_discarded) и фантомы (payment_await/error_assembling/disputed/
// signer_signing_in_progress) сюда НЕ входят — они отсекаются на бэке через statuses.
export const PRETENSION_REPORT_FILTER_STATUSES = [
  'draft',
  'control_await',
  'signer_signing_await',
  'supplier_approve_await',
  'accounting_approve_await',
  'payment_done',
  'problem',
  'recalled',
] as const;

export type PretensionReportFilterStatus = (typeof PRETENSION_REPORT_FILTER_STATUSES)[number];

// Канонический → сырые статусы для запроса/тоталов. Единый источник истины.
// Только signer_signing_await раскрывается в 2 кода (этап подписи + Диадок), остальные 1:1.
export const CANONICAL_TO_RAW_STATUSES: Record<PretensionReportFilterStatus, string[]> = {
  draft: ['draft'],
  control_await: ['control_await'],
  signer_signing_await: ['signer_signing_await', 'diadoc_signing_await'],
  supplier_approve_await: ['supplier_approve_await'],
  accounting_approve_await: ['accounting_approve_await'],
  payment_done: ['payment_done'],
  problem: ['problem'],
  recalled: ['recalled'],
};

// Полный «видимый whitelist сырых» (9 кодов) — объединение правого столбца. Шлём его, когда
// фильтр статусов пуст, чтобы бэк исключил created/draft_discarded и тоталы считались по видимым.
export const VISIBLE_RAW_STATUSES: string[] = Object.values(CANONICAL_TO_RAW_STATUSES).flat();

const RAW_TO_CANONICAL: Record<string, string> = Object.entries(CANONICAL_TO_RAW_STATUSES)
  .reduce((acc, [canonical, raws]) => {
    raws.forEach((raw) => { acc[raw] = canonical; });
    return acc;
  }, {} as Record<string, string>);

// Сырой статус → канонический (для склейки чипов «По статусам»: diadoc_signing_await →
// signer_signing_await). Неизвестный сырой возвращается как есть.
export function canonicalStatus(raw: string): string {
  return RAW_TO_CANONICAL[raw] ?? raw;
}

export interface PretensionReportApiRow {
  id: number;
  number: string;
  type: 'damage' | 'fine';
  platformId: number;
  platformName: string | null;
  supplierId: number;
  supplierLegalName: string | null;
  createdAt: string;
  currentStatus: string;
  currentStatusSince: string;
  totalWithoutTax: number;
  isFinal: boolean;
  currentStatusDurationSeconds: number;
  totalLifecycleDurationSeconds: number | null;
  statusHistory: Array<{
    fromStatus: string | null;
    toStatus: string;
    changedAt: string;
    changedBy: string | null;
    comment: string | null;
  }>;
}

export interface PretensionReportTotals {
  totalCount: number;
  totalAmount: number;
  byStatus: Array<{ status: string; count: number; amount: number }>;
  byType: Array<{ type: 'damage' | 'fine'; count: number; amount: number }>;
}

export interface PretensionReportApiResponse {
  data: {
    rows: PretensionReportApiRow[];
    totals: PretensionReportTotals;
    pagination: { total: number; pageFrom: number; pageSize: number };
    meta?: { historySinceDate?: string };
  };
}

export interface PretensionReportTableHistoryRow {
  fromStatusLabel: string | null;
  toStatusLabel: string;
  changedAtFormatted: string;
  changedAtIso: string;
  changedBy: string | null;
  comment: string | null;
}

export interface PretensionReportTableRow {
  id: number;
  number: string;
  type: string;
  platformName: string;
  supplierLegalName: string;
  createdAt: string;
  createdAtIso: string;
  amount: string;
  _amount: number;
  currentStatus: string;
  currentStatusSince: string;
  currentStatusSinceIso: string;
  duration: string;
  _duration: number | null;
  statusHistory: PretensionReportTableHistoryRow[];
}

export function humanizePretensionStatus(
  code: string,
  i18n: (k: string) => string,
  role?: 'supplier',
): string {
  // SUPPLIER override (БЦ3.3 followup FML-wccv.9): SUPPLIER не должен видеть внутренние
  // FML-метки ("Отправлено в SAP", "На согласовании с поставщиком") + мужской род у "Отозван"
  // непонятен бизнесово. Для остальных ролей (HR/SUPERVISOR/ACCOUNTANT/SIGNER/CONTROLLING)
  // existing behavior сохраняется без regression. payment_done ('Оплачено') one-fits-all,
  // отдельный supplier override не нужен.
  if (role === 'supplier') {
    if (code === 'supplier_approve_await') return i18n('supplier_approve_await_supplier_alias');
    if (code === 'payment_await') return i18n('payment_await_supplier_alias');
    if (code === 'recalled') return i18n('recalled_supplier_alias');
  }
  // diadoc_signing_await — подпись отправлена, ждём подтверждения из Диадока. Свой ключ:
  // голый diadoc_signing_await в локали занят подписью листа учёта.
  if (code === 'diadoc_signing_await') return i18n('pretension_status_diadoc_signing_await');
  // SAP-alias вынесен в i18n-ключ — корректно локализуется в en/ru.
  if (code === 'payment_await') return i18n('pretension_status_payment_await_alias');
  return i18n(code);
}

export function formatDurationCompact(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return '< 1 мин';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин`;
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}ч ${m}м`;
  }
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  return `${d}д ${h}ч`;
}

export function formatMoneyRu(value: number | null): string {
  if (value === null || value === undefined) return '';
  const fixed = value.toFixed(2);
  const [intPart, frac] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped}.${frac} ₽`;
}

export function formatDateTimeDdMmYyyyHhMm(raw: string | null): string {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}

// Та же история приходит и в карточку претензии (задача 11991).
export function mapStatusHistory(
  rows: PretensionReportApiRow['statusHistory'] | undefined,
  i18n: (k: string) => string,
): PretensionReportTableHistoryRow[] {
  return (rows ?? []).map((h) => ({
    fromStatusLabel: h.fromStatus ? humanizePretensionStatus(h.fromStatus, i18n) : null,
    toStatusLabel: humanizePretensionStatus(h.toStatus, i18n),
    changedAtFormatted: formatDateTimeDdMmYyyyHhMm(h.changedAt),
    changedAtIso: h.changedAt,
    changedBy: h.changedBy,
    comment: h.comment,
  }));
}

export function mapRowToTableRow(
  r: PretensionReportApiRow,
  i18n: (k: string) => string,
): PretensionReportTableRow {
  // null/undefined пробрасываем как есть — formatDurationCompact отдаст «—».
  // Коэрсия в 0 даёт ложное «< 1 мин» для финальных претензий без lifecycle-метрики.
  const durationSeconds: number | null = r.isFinal
    ? (r.totalLifecycleDurationSeconds ?? null)
    : r.currentStatusDurationSeconds;
  return {
    id: r.id,
    number: r.number,
    type: i18n(r.type),
    platformName: r.platformName ?? '',
    supplierLegalName: r.supplierLegalName ?? '',
    createdAt: formatDateDdMmYyyy(r.createdAt),
    createdAtIso: parseDateIso(r.createdAt),
    amount: formatMoneyRu(r.totalWithoutTax),
    _amount: r.totalWithoutTax,
    currentStatus: humanizePretensionStatus(r.currentStatus, i18n),
    currentStatusSince: formatDateTimeDdMmYyyyHhMm(r.currentStatusSince),
    currentStatusSinceIso: r.currentStatusSince,
    duration: formatDurationCompact(durationSeconds),
    _duration: durationSeconds,
    statusHistory: mapStatusHistory(r.statusHistory, i18n),
  };
}
